import { SaveOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment, ViewForSiderFieldsFragment } from '@salescore/client-api'
import { PickableSearchableList, useCanForView } from '@salescore/client-common'
import { CORE_CONSTANT, type ViewConfigKpiParameter, type ViewConfigKpiPivot } from '@salescore/core'
import { ButtonWithTooltip, generateRandomString, Kanban, type KanbanGroup } from '@salescore/frontend-common'
import { App, message, type ModalFuncProps } from 'antd'
import { Array, Order, pipe, Record } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column'
import { kpiParameterAtom, kpiPivotPickedPresetNameAtom } from '../../../recoil/navigation/atoms'
import { useLoadingState } from '../../../recoil/records/hooks'
import { viewsContextAtom } from '../../../recoil/view/atoms'
import { useViewConfigKpiPivot } from '../../../recoil/view/hooks/useViewConfigKpiPivot'
import { useKpiViews, useSearchParentViewGroups } from '../../../recoil/view/selectors/viewsRelatedSelector'
import { buildNewKpiFormModalProperties } from '../../../rsheet/components/modal/NewKpiFormModal'

interface KpiPivotKpisFormModalProperties {
  onFinish: () => void
  columns: Array<RSheetColumn<RSheetRecordNode>>
  view: ViewFieldsFragment
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}

export function buildKpiPivotKpisFormModalProperties({
  onFinish,
  columns,
  view,
  setHasChangeToViewSchema,
}: KpiPivotKpisFormModalProperties): ModalFuncProps {
  return {
    icon: null,
    title: t(`ダッシュボードに表示するKPIの編集`),
    width: '90%',
    cancelText: t(`閉じる`),
    okButtonProps: { style: { display: 'none' } },
    style: {
      maxWidth: 1800,
    },
    closable: true,
    maskClosable: true,
    destroyOnClose: true,
    footer: null,
    content: (
      <KpiPivotKpisForm
        onFinish={onFinish}
        columns={columns}
        view={view}
        setHasChangeToViewSchema={setHasChangeToViewSchema}
      />
    ),
  }
}

export const KpiPivotKpisForm = ({
  onFinish,
  columns,
  view,
  setHasChangeToViewSchema,
}: KpiPivotKpisFormModalProperties): ReactElement => {
  const [kpiPivotParameter, setKpiPivotParameter] = useRecoilState(kpiParameterAtom)
  const canCreate = useCanForView('create', view)
  const canUpdate = useCanForView('update', view)
  const loading = useLoadingState()
  const [config, setConfig] = useViewConfigKpiPivot()
  const { onAddResource } = useRecoilValue(viewsContextAtom)
  const { modal } = App.useApp()
  const kpiViews = useKpiViews()
  const searchParentViewGroups = useSearchParentViewGroups()
  const records = kpiViews
    // 公開されており、削除されていないものに絞る
    // ただし、このビュー自体が非公開だったら、非公開KPIも選択可能
    .filter((x) => (view.private || !x.private) && !x.archived)
    .map((kpiView) => ({
      id: kpiView.id,
      name: kpiView.name,
      path: searchParentViewGroups(kpiView.viewGroupId)
        .toReversed()
        .map((viewGroup) => viewGroup.name),
      createdBy: kpiView.createdBy?.name,
    }))
  const [groups, setGroups] = useState(() => {
    const kpis = config?.kpis ?? []
    return getSortedKpiGroups({ kpis, kpiViews, pivot: kpiPivotParameter.pivot })
  })
  const [pickedPresetName] = useRecoilState(kpiPivotPickedPresetNameAtom)
  const [copiedKpis, setCopiedKpis] = useState<Array<{ viewId: string; kpiGroupName: string }>>()
  const [needsSaving, setNeedsSaving] = useState(false)

  const generateRandomGroupName = (length = 5): string => {
    // eslint-disable-next-line no-useless-assignment
    let temporaryGroupName = ''
    do {
      temporaryGroupName = `${t(`グループ`)}-${generateRandomString(length)}`
    } while (groups.map((g) => g.id).includes(temporaryGroupName))
    return temporaryGroupName
  }

  const save = async (): Promise<void> => {
    loading.setTrue()
    try {
      if (config === undefined) {
        loading.setFalse()
        return
      }

      // eslint-disable-next-line sonarjs/function-return-type
      const kpis = groups.flatMap((group) => {
        // KPIグループにKPIが存在しない場合に空グループが作れるように、viewIdに重複しない仮の値を入れる
        if (group.items.length === 0) {
          return {
            viewId: `${view.id}-${group.label}-noneKpiGroup`,
            kpiGroupName: group.label,
          }
        }
        return group.items.map((kpi) => ({
          viewId: kpi.id,
          kpiGroupName: group.label,
        }))
      })
      const getNewKpiPivotParameter = (oldValue: ViewConfigKpiParameter) => ({
        ...oldValue,
        pivot: {
          ...oldValue.pivot,
          rows: oldValue.pivot.rows.map((d) => {
            if (d.key !== CORE_CONSTANT.KPI_PIVOT_KPI_GROUP_NAME_COLUMN_NAME) {
              return d
            }
            return {
              ...d,
              sortedValues: kpis.map((x) => x.kpiGroupName),
            }
          }),
          columns: oldValue.pivot.columns.map((d) => {
            if (d.key !== CORE_CONSTANT.KPI_PIVOT_KPI_GROUP_NAME_COLUMN_NAME) {
              return d
            }
            return {
              ...d,
              sortedValues: kpis.map((x) => x.kpiGroupName),
            }
          }),
        },
      })
      const deleteInvisibleKpiIds = (invisibleKpiIds: string[]): string[] =>
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        invisibleKpiIds.filter((id) => kpis.find((kpi) => kpi.viewId === id))
      setConfig({
        ...config,
        kpis,
        presets: config.presets?.map((p) => {
          const isPickedPreset = p.name === pickedPresetName
          // config.kpisは全プリセット共通のため、KPIグループ変更時は全プリセットのsortedValuesを更新する
          const newPresetParameter = getNewKpiPivotParameter(isPickedPreset ? kpiPivotParameter : p.parameter)
          return {
            ...p,
            parameter: {
              ...newPresetParameter,
              invisibleKpiIds: deleteInvisibleKpiIds(newPresetParameter.invisibleKpiIds ?? []),
              sorter: isPickedPreset ? {} : newPresetParameter.sorter,
            },
          }
        }),
      })
      setKpiPivotParameter((oldValue) => {
        const newKpiPivotParameter = getNewKpiPivotParameter(oldValue)
        return {
          ...newKpiPivotParameter,
          invisibleKpiIds: deleteInvisibleKpiIds(newKpiPivotParameter.invisibleKpiIds ?? []),
          sorter: {
            columnKeys: undefined,
          },
        }
      })
      setHasChangeToViewSchema(true)
      await onAddResource({ viewIds: kpis.map((x) => x.viewId) })
      void message.success(t(`KPI設定を保存しました。`))
      if (pickedPresetName !== undefined) {
        void message.success(t(`集計プリセットを保存しました`))
      }
      loading.setFalse()
    } catch (error) {
      loading.setFalse()
      if (error instanceof Error) {
        void message.error(`${t(`エラーが発生しました。`)}${error.message}`)
      } else {
        void message.error(t(`エラーが発生しました。`))
      }
    }
  }

  const onSave = async (): Promise<void> => {
    await save()
    onFinish()
  }

  useEffect(() => {
    if (copiedKpis === undefined) {
      return
    }

    const newItems = copiedKpis
      .map((ck) => {
        const item = kpiViews.find((kv) => kv.id === ck.viewId)
        if (item === undefined) {
          // eslint-disable-next-line array-callback-return
          return
        }
        return [
          ck.kpiGroupName,
          {
            id: item.id,
            label: item.name,
            item,
            checked: false,
          },
        ] as const
      })
      .filter(isNotNullish)

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setGroups((previous) =>
      previous.map((g) => ({
        ...g,
        items: Array.dedupeWith(
          [
            // eslint-disable-next-line max-nested-callbacks
            ...g.items.map((index) => ({ ...index, checked: false })),
            // eslint-disable-next-line max-nested-callbacks
            ...newItems.filter((ni) => ni[0] === g.id).map((ni) => ni[1]),
          ],
          // eslint-disable-next-line max-nested-callbacks
          (a, b) => a.id === b.id,
        ),
      })),
    )

    setCopiedKpis(undefined)
    setNeedsSaving(true)
  }, [kpiViews])

  useEffect(() => {
    if (!needsSaving) {
      return
    }

    void save()
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNeedsSaving(false)
  }, [needsSaving])

  if (config === undefined) {
    return <></>
  }
  return (
    <div className="w-full">
      <p>
        {t(`ダッシュボードに表示したいKPIを選択してください。`)}
        <br />
        {t(`ドラッグアンドドロップで並び替えを行うこともできます。`)}
      </p>
      <Kanban
        groups={groups}
        setGroups={setGroups}
        addGroupLabel={t(`KPIグループを追加`)}
        addItemLabel={t(`KPIを追加`)}
        addGroup={() => {
          setGroups((groups) => {
            const newGroupName = generateRandomGroupName()
            return [
              ...groups,
              {
                id: newGroupName,
                label: newGroupName,
                group: newGroupName,
                items: [],
              },
            ]
          })
        }}
        addItem={(groupId) => {
          const m = modal.confirm({
            icon: null,
            width: '90%',
            cancelText: t(`閉じる`),
            okButtonProps: { style: { display: 'none' } },
            title: <div>{t(`追加するKPIの選択`)}</div>,
            style: { top: '3%', maxWidth: 600 },
            footer: null,
            closable: true,
            maskClosable: true,
            destroyOnClose: true,
            content: (
              <div>
                {t(`{{groupId}}に追加するKPIを選択してください。`, { groupId })}
                <PickableSearchableList
                  records={records}
                  initialValues={groups.find((x) => x.id === groupId)?.items.map((x) => x.id) ?? []}
                  onAdd={
                    canCreate
                      ? (addValue): void => {
                          const m = modal.confirm(
                            buildNewKpiFormModalProperties({
                              showAllPresetsOption: false,
                              isVisibleInAllPresets: true,
                              onFinish: (kpiId) => {
                                addValue(kpiId)
                                m.destroy()
                              },
                            }),
                          )
                        }
                      : undefined
                  }
                  onFinish={(picked) => {
                    const pickedIds = new Set(picked.map((x) => x.id))
                    setGroups((groups) =>
                      groups.map((group) => {
                        if (group.id !== groupId) {
                          return {
                            ...group,
                            // eslint-disable-next-line sonarjs/no-nested-functions
                            items: group.items.filter((x) => !pickedIds.has(x.id)),
                          }
                        }
                        return {
                          ...group,
                          items:
                            // ...group.items,
                            pipe(
                              picked,
                              // eslint-disable-next-line sonarjs/no-nested-functions
                              Array.map((kpiView) => ({
                                id: kpiView.id,
                                label: kpiView.name,
                                item: kpiView,
                                // eslint-disable-next-line max-nested-callbacks
                                checked: group.items.find((index) => index.id === kpiView.id)?.checked ?? false,
                              })),
                              // eslint-disable-next-line sonarjs/no-nested-functions
                              Array.dedupeWith((a, b) => a.id === b.id),
                            ),
                        }
                      }),
                    )
                    m.destroy()
                  }}
                />
              </div>
            ),
          })
        }}
        editGroup={(groupId, groupName) => {
          const otherGroups = groups.filter((x) => x.id !== groupId)
          if (otherGroups.map((x) => x.label).includes(groupName)) {
            void message.error(t(`同じグループ名を2つ以上設定することはできません`))
            return
          }
          setGroups((groups) =>
            groups.map((group) => {
              if (group.id !== groupId) {
                return group
              }
              return {
                id: groupName,
                label: groupName,
                group: groupName,
                items: group.items,
              }
            }),
          )
        }}
      />
      <div className="relative mt-4 flex flex-row-reverse">
        <ButtonWithTooltip
          tooltipTitle={t(`権限がないため保存できません`)}
          showTooltip={!canUpdate}
          disabled={!canUpdate}
          type="primary"
          icon={<SaveOutlined />}
          loading={loading.isTrue}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
          onClick={async () => {
            await onSave()
          }}
        >
          {t(`保存`)}
        </ButtonWithTooltip>
      </div>
    </div>
  )
}

function getKpiGroupDimension(pivot: ViewConfigKpiParameter['pivot']) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const columns = pivot.columns ?? []
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const rows = pivot.rows ?? []
  const dimension = [...columns, ...rows].find((x) => x.key === CORE_CONSTANT.KPI_PIVOT_KPI_GROUP_NAME_COLUMN_NAME)
  return dimension
}

function getSortedKpiGroups({
  kpis,
  kpiViews,
  pivot,
}: {
  kpis: ViewConfigKpiPivot['kpis']
  kpiViews: ViewForSiderFieldsFragment[]
  pivot: ViewConfigKpiParameter['pivot']
}) {
  function addCheckedFieldToItem<G, I>(g: KanbanGroup<G, I>): KanbanGroup<G, I> {
    return {
      ...g,
      items: g.items.map((index) => ({ ...index, checked: false })),
    }
  }

  const groups = getKpiGroups({ kpis, kpiViews })
  const dimension = getKpiGroupDimension(pivot)

  // view と preset の保存は別々のタイミングで行えるので、view の kpi groups と preset の kpi groups の集合に差分が生じる場合がある
  // ユニークな集合の差分がない場合のみ、ソート可能とする
  const shouldSort =
    pipe(
      groups,
      Array.map((x) => x.label),
      Array.dedupeWith((a, b) => a === b),
      Array.sort(Order.String),
      Array.join(','),
    ) ===
    pipe(
      dimension?.sortedValues ?? [],
      Array.dedupeWith((a, b) => a === b),
      Array.sort(Order.String),
      Array.join(','),
    )
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (shouldSort && dimension?.sortedValues) {
    return pipe(
      dimension.sortedValues,
      Array.map((value) => groups.find((x) => x.label === value)),
      Array.filter(isNotNullish),
      Array.dedupeWith((a, b) => a.label === b.label),
      Array.map((element) => addCheckedFieldToItem(element)),
    )
  }

  return groups.map((element) => addCheckedFieldToItem(element))
}

function getKpiGroups({
  kpis,
  kpiViews,
}: {
  kpis: ViewConfigKpiPivot['kpis']
  kpiViews: ViewForSiderFieldsFragment[]
}) {
  const groups = pipe(
    kpis,
    Array.groupBy((x) => x.kpiGroupName ?? CORE_CONSTANT.KPI_GROUP_DEFAULT_NAME),
    Record.toEntries,
    Array.map(([groupName, kpis]): KanbanGroup<unknown, unknown> => ({
      id: groupName,
      label: groupName,
      group: groupName, // 使われない想定
      items: Array.flatMapNullishOr(kpis, (kpi) => {
        const kpiView = kpiViews.find((x) => x.id === kpi.viewId)
        if (kpiView === undefined) {
          // eslint-disable-next-line sonarjs/todo-tag
          return // TODO
        }
        return {
          id: kpi.viewId,
          label: kpiView.name,
          item: kpiView,
        }
      }),
    })),
  )

  if (groups.length === 0) {
    // i18n: 影響反映の確認が必要なため一旦保留
    return [{ id: `メインKPI`, label: `メインKPI`, group: `メインKPI`, items: [] }]
  }
  return groups
}
